import { z } from "zod";

import {
  BiddingStrategy,
  OptimizationTarget,
  PERIODS,
  PERIODS_CP,
} from "../../types/optimization-rule";

export const optimizationFormSchema = z
  .object({
    name: z.string().min(1, "Name is required."),
    description: z.string().optional(),
    target: z.nativeEnum(OptimizationTarget),
    timeframe: z.string().optional(),
    biddingStrategy: z.nativeEnum(BiddingStrategy).optional(),
    customValueCutoff: z.number().min(0).max(1).nullable().optional(),
    customCpaCutoff: z.number().min(0).max(1).optional(),
    customFopWeight: z.number().min(0).max(1).optional(),
    customCltvWeight: z.number().min(0).max(1).optional(),
    customCltpWeight: z.number().min(0).max(1).optional(),
    maxValue: z.number().min(0).optional(),
    minValuePercentile: z.number().min(0).max(1).optional(),
    maxValuePercentile: z.number().min(0).max(1).optional(),
    avgOrderValue: z.number().min(0).optional(),
  })
  .refine(
    (data) =>
      data.target === OptimizationTarget.FOP || data.timeframe !== undefined,
    {
      message:
        "Timeframe is required for all targets except First-Order Profit",
      path: ["timeframe"],
    },
  )
  .refine(
    (data) =>
      data.target === OptimizationTarget.FOP ||
      data.biddingStrategy !== undefined,
    {
      message:
        "Bidding strategy is required for all targets except First-Order Profit",
      path: ["biddingStrategy"],
    },
  )
  .refine(
    (data) => {
      if (data.target === OptimizationTarget.FOP) return true;
      if (!data.timeframe) return false;
      const numVal = Number(data.timeframe);
      if (data.target === OptimizationTarget.CP) {
        return (PERIODS_CP as readonly number[]).includes(numVal);
      }
      return (PERIODS as readonly number[]).includes(numVal);
    },
    {
      message: "Invalid timeframe for selected target",
      path: ["timeframe"],
    },
  );

export type OptimizationFormData = z.infer<typeof optimizationFormSchema>;
